import { FormControlLabel, Icon, IconButton, Stack, Switch } from '@mui/material';
import { Box } from '@mui/material';
import { Typography } from '@mui/material';
import { Tooltip } from '@mui/material';
import {
  ApiError,
  DEFAULT_DATE_FORMAT_FNS,
  SchoolSettingsResponse,
  SHORT_FORMATTED_DATE_FORMAT_FNS,
  TermsAndConditionsSave,
  UpdateSchoolTermsAndConditionsResponse,
  useExportTermsAndConditions,
} from '@schooly/api';
import { TermsAndConditionsUpload } from '@schooly/api';
import { useNotifications } from '@schooly/components/notifications';
import { DELAY_BEFORE_HIDE_ICON_DONE } from '@schooly/constants';
import {
  CloseIcon,
  DocumentIcon,
  EditIcon,
  InformationBlock,
  NewTabIcon,
  PlusIcon,
  SimpleButton,
  Spin,
  SubmitIcon,
  UnpublishIcon,
} from '@schooly/style';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { format } from 'date-fns';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form-lts';
import { FormattedMessage, useIntl } from 'react-intl';

import { openFile } from '../../../../utils/openFile';
import { AddDocumentButton, MAX_FILE_SIZE_MB } from './AddDocumentButton';

export type UploadStatus = 'success' | 'uploadError' | 'sizeError' | 'formatError';
type UploadResult = {
  status: UploadStatus;
  documentData?: { name: string; date: string };
};

export type SchoolTermsAndConditionsForm = TermsAndConditionsSave;

const isTermsAndConditionsUpload = (
  document: Partial<TermsAndConditionsUpload>,
): document is TermsAndConditionsUpload => {
  return Boolean(document.data && document.name);
};

type SchoolTermsAndConditionsContentProps = {
  disabled: boolean;
  data: SchoolSettingsResponse['terms_and_conditions'];
  isUpdating: boolean;
  header: React.ReactNode;
  onSubmit: (
    data: SchoolTermsAndConditionsForm,
  ) => Promise<UpdateSchoolTermsAndConditionsResponse | undefined>;
};

export const SchoolTermsAndConditionsContent: FC<SchoolTermsAndConditionsContentProps> = ({
  disabled,
  data,
  isUpdating,
  header,
  onSubmit,
}) => {
  const { $t } = useIntl();
  const { showError } = useNotifications();
  const { mutateAsync: exportDocument, isLoading: isExporting } = useExportTermsAndConditions();

  const form = useForm<SchoolTermsAndConditionsForm>({ defaultValues: { enabled: data.enabled } });

  const [updateResult, setUpdateResult] = useState<UploadResult | null>(null);

  const currentDocument = useMemo(
    () => data.documents.find((doc) => doc.is_current),
    [data.documents],
  );

  const enabled = form.watch('enabled');
  const document = form.watch('document');

  const isUploading = isUpdating && document;

  const openDocument = useCallback(async () => {
    if (!currentDocument) return;

    try {
      const { data } = await exportDocument({ id: currentDocument.id });
      if (data) {
        openFile(data);
      }
    } catch (error) {
      showError(error as ApiError);
    }
  }, [currentDocument, exportDocument, showError]);

  const resetUpdateResult = useCallback(() => {
    setTimeout(() => setUpdateResult(null), DELAY_BEFORE_HIDE_ICON_DONE);
  }, []);

  const handleSubmit = useCallback<SubmitHandler<SchoolTermsAndConditionsForm>>(
    async (data) => {
      try {
        const result = await onSubmit(data);
        if (result) {
          setUpdateResult({
            status: 'success',
            documentData:
              data.document && result.document
                ? {
                    name: result.document.name,
                    date: format(new Date(result.document.uploaded_at), DEFAULT_DATE_FORMAT_FNS),
                  }
                : undefined,
          });
          resetUpdateResult();
        }
      } catch (error) {
        if (data.document) {
          setUpdateResult({ status: 'uploadError' });
          resetUpdateResult();
          return;
        }

        form.reset({ enabled: !data.enabled });
        showError(error as ApiError);
      }
    },
    [form, onSubmit, resetUpdateResult, showError],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: Legacy code
  useEffect(() => {
    const subscription = form.watch(({ enabled, document }, { name }) => {
      if (!name) return;

      switch (name) {
        case 'enabled':
          if (enabled && !document && !currentDocument) return;
          handleSubmit({ enabled: !!enabled });
          break;
        case 'document':
          if (document && isTermsAndConditionsUpload(document)) {
            handleSubmit({ enabled: !!enabled, document });
          }
          break;
      }
    });

    return () => subscription.unsubscribe();
  }, [currentDocument, data.enabled, document, form, handleSubmit, onSubmit]);

  const renderDocument = () => {
    if (isUploading || isExporting) {
      return (
        <InformationBlock sx={{ px: 1.5, py: 1.25, justifyContent: 'flex-start' }}>
          <Icon sx={{ height: 22, color: 'primary.main' }}>
            <Spin />
          </Icon>
        </InformationBlock>
      );
    }

    const { status, documentData } = updateResult ?? {};

    if (status === 'success' && documentData) {
      const { name, date } = documentData;

      return (
        <InformationBlock
          sx={{ px: 1.5, py: 1.25, backgroundColor: 'success.superLight', color: 'success.main' }}
        >
          <Stack direction="row" gap={1} sx={{ flex: 1 }}>
            <Icon>
              <SubmitIcon />
            </Icon>
            {name && (
              <Typography variant="h3" color="success.main">
                {name}
              </Typography>
            )}
          </Stack>
          {date && (
            <Typography variant="body2" sx={{ flex: 1 }}>
              {format(newDateTimezoneOffset(date), SHORT_FORMATTED_DATE_FORMAT_FNS)}
            </Typography>
          )}
        </InformationBlock>
      );
    }

    if (updateResult && status !== 'success') {
      return (
        <InformationBlock
          sx={{
            px: 1.5,
            py: 1.25,
            backgroundColor: 'error.superLight',
            color: 'error.main',
            justifyContent: 'space-between',
          }}
        >
          <Stack direction="row" gap={1}>
            <Icon>
              <UnpublishIcon />
            </Icon>
            <Typography variant="h3" color="error.main">
              {$t({ id: `school-TermsAndConditions-Error-${status}` }, { size: MAX_FILE_SIZE_MB })}
            </Typography>
          </Stack>
          <IconButton
            inverse
            sx={{ ':hover': { color: 'error.main' } }}
            onClick={() => setUpdateResult(null)}
          >
            <CloseIcon />
          </IconButton>
        </InformationBlock>
      );
    }

    if (!currentDocument) {
      return (
        <InformationBlock
          sx={{ px: 0, py: 1.25, backgroundColor: 'none', justifyContent: 'flex-start' }}
        >
          <AddDocumentButton
            startIcon={<PlusIcon />}
            setUploadStatus={(status) => {
              setUpdateResult({ status });
              resetUpdateResult();
            }}
            setDocument={(document) => form.setValue('document', document)}
          >
            <FormattedMessage id="school-TermsAndConditions-UploadPDF" />
          </AddDocumentButton>
        </InformationBlock>
      );
    }

    return (
      <InformationBlock
        sx={{
          px: 1.5,
          py: 1.25,
          '& .MuiButton-endIcon, .editButton': {
            visibility: 'hidden',
          },
          backgroundColor: 'none',
          ':hover': {
            backgroundColor: 'common.lightBg',
            color: 'primary.main',
            '& .MuiButtonBase-root:not(:hover)': {
              color: 'primary.main',
            },
            '& .MuiButton-endIcon, .editButton': {
              visibility: 'visible',
            },
          },
        }}
      >
        <Box flex={1}>
          <SimpleButton
            inverse
            onClick={openDocument}
            startIcon={<DocumentIcon />}
            endIcon={<NewTabIcon />}
            sx={(theme) => ({
              ':hover': { textDecoration: 'underline' },
              '& .MuiButton-endIcon .svg-icon': {
                fontSize: theme.spacing(2),
              },
            })}
          >
            {currentDocument.name}
          </SimpleButton>
        </Box>

        <Stack direction="row" gap={1} flex={1} justifyContent="space-between" alignItems="center">
          <Typography variant="body2">
            {format(new Date(currentDocument.uploaded_at), SHORT_FORMATTED_DATE_FORMAT_FNS)}
          </Typography>
          <AddDocumentButton
            inverse
            startIcon={<EditIcon />}
            className="editButton"
            setUploadStatus={(status) => {
              setUpdateResult({ status });
              resetUpdateResult();
            }}
            setDocument={(document) => form.setValue('document', document)}
          >
            <FormattedMessage id="school-TermsAndConditions-ChangePDF" />
          </AddDocumentButton>
        </Stack>
      </InformationBlock>
    );
  };

  return (
    <FormProvider {...form}>
      <form>
        <Stack direction="row">
          {header}

          <FormControlLabel
            sx={{ m: 0 }}
            disabled={disabled}
            control={
              <Tooltip
                componentsProps={{ tooltip: { sx: { p: 1.25 } } }}
                title={
                  disabled ? (
                    <Typography>
                      <FormattedMessage id="school-TermsAndConditions-EnableReEnrollment" />
                    </Typography>
                  ) : undefined
                }
              >
                <Box>
                  <Switch
                    checked={enabled}
                    disabled={disabled}
                    onChange={(e) => {
                      form.setValue('enabled', e.target.checked);
                    }}
                  />
                </Box>
              </Tooltip>
            }
            label={
              <Typography variant="h3" color={enabled ? undefined : 'common.grey'}>
                <FormattedMessage id="school-TermsAndConditions-SignTermsAndConditions" />
              </Typography>
            }
          />
        </Stack>

        {enabled && <Box mt={2.5}>{renderDocument()}</Box>}
      </form>
    </FormProvider>
  );
};
